import { Logger } from '@services/logger/abstract.logger';

import isEqual from 'lodash-es/isEqual';
import {
    BehaviorSubject,
    Observable,
} from 'rxjs';
import {
    distinctUntilChanged,
    skip,
} from 'rxjs/operators';

export interface IState<T> {
    value: Readonly<T>;
    value$: Observable<Readonly<T>>;
    change$: Observable<Readonly<T>>;
}

export interface INullableState<T> {
    value: Readonly<T> | null;
    value$: Observable<Readonly<T> | null>;
    change$: Observable<Readonly<T> | null>;
}

export interface IFetchableState<T> {
    fetch(): Observable<Readonly<T>>;
}

abstract class BaseStateManager<TState, TExposedState = TState> {

    protected abstract _state: BehaviorSubject<TState>;

    private _value$?: Observable<Readonly<TExposedState>>;
    private _change$?: Observable<Readonly<TExposedState>>;

    public get value() {
        return this._state.getValue() as unknown as Readonly<TExposedState>;
    }

    public get value$() {
        return this._value$ ?
            this._value$ :
            this._value$ = this._state.pipe<Readonly<TExposedState>>(
                distinctUntilChanged(isEqual),
            );
    }

    public get change$() {
        return this._change$ ?
            this._change$ :
            this._change$ = this.value$.pipe(
                skip(1),
            );
    }

    protected _logStateChanges(managerName: string, logger: Logger) {
        this._state.subscribe(
            (value: any) => logger.log(`${managerName.toUpperCase()} state change:`, value),
        );
    }
}

export abstract class StateManager<TState>
    extends BaseStateManager<TState> { }

export abstract class NullableStateManager<TState>
    extends BaseStateManager<TState | null> { }

/**
 * A state manager whose state can be null, but it never exposes that fact. Its state should become non-null inside the app initializer.
 */
export abstract class NonPublicallyNullableStateManager<T>
    extends BaseStateManager<T | null, T> { }
